import { Injectable } from '@angular/core';
import { lastValueFrom } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { ToastrService } from 'ngx-toastr';
import { SseService } from './SseService';
import { OAuthService } from './oauth.service';
import { BrokerService } from './broker.services';
import { BrokerAdapterFactory } from './broker-adapter-factory.service';
import { IbkrOrdenService } from './ibkr-orden.service';
import { OrigenOrden } from '../shared/pipes/origen-orden.pipe';
import { motivoRechazoMetaApi } from './meta-api-error-messages';
import { MetaApiService } from './meta-api.service';

/** Cartera sobre la que se gestionan los SL/TP. `operaciones` = operaciones transformadas de la cartera. */
export interface SlTpContexto {
  idcartera: number;
  cartera: string;
  tipoBroker: string;
  metaApiAccountId: string;
  operaciones: any[];
  panelClass?: string;
}

/**
 * Establecer / actualizar / cancelar Stop Loss (STP) y Take Profit (LMT) de las operaciones abiertas
 * de una cartera del broker (IBKR y MetaAPI). Recibe operaciones de `ColaOrdenesService.operaciones$`
 * ya pasadas por `transformarOperaciones`. `pricestp`/`pricelmt` deben ser los del SL/TP vivo
 * (0 si no hay): los de operaciones/{idcartera} pueden ser de otra posición del mismo isooper.
 * `precioactual` (opcional) = cotización actual, para no colocar un nivel del lado equivocado.
 */
@Injectable({ providedIn: 'root' })
export class SlTpBrokerService {

  constructor(
    private sse: SseService,
    private oauthService: OAuthService,
    private brokerService: BrokerService,
    private brokerAdapterFactory: BrokerAdapterFactory,
    private ibkrOrden: IbkrOrdenService,
    private cookies: CookieService,
    private toastr: ToastrService,
    private metaApi: MetaApiService,
  ) { }

  private get licencia(): string {
    return this.cookies.get('auth_token');
  }

  /** Filtra por cartera y normaliza estado / tipoOperacion (en bajista ENTRADA y SALIDA se invierten). */
  transformarOperaciones(operaciones: any[], idcartera: number): any[] {
    return (operaciones || [])
      .filter(op => op.idcartera === idcartera)
      .map(op => ({
        ...op,
        idmodelo: op.idmodelo || 0,
        fecha: op.fechaOperacion,
        estado: op.estado ? op.estado.toUpperCase() : 'N/A',
        tipoOperacion: op.bajista ?
          (op.tipoOperacion === 'ENTRADA' ? 'SALIDA' : 'ENTRADA') :
          op.tipoOperacion
      }));
  }

  /** 5 decimales para CFD, 2 para el resto. */
  formatPrice(value: number | undefined, element: any): string {
    if (value === undefined || value === null) return '0.00';
    const secType = element?.sectype || element?.secType || '';
    const isCFD = secType.includes('CFD') || secType.includes('cfd');
    return value.toFixed(isCFD ? 5 : 2);
  }

  private wallet(ctx: SlTpContexto): { accountId: string, wallet: any } {
    const accountId = ctx.metaApiAccountId || ctx.cartera;
    return { accountId, wallet: { tipoBroker: 'METAAPI', metaApiAccountId: accountId, nombre: accountId } };
  }

  /** El isooper puede repetirse entre sentidos: la salida tiene que ser de la misma posición. */
  private tieneSalida(ctx: SlTpContexto, el: any): boolean {
    return ctx.operaciones.some(op => op.isooper === el.isooper && !!op.bajista === !!el.bajista && op.tipoOperacion === 'SALIDA');
  }

  /**
   * Precio (redondeado) del SL/TP a colocar, o null con aviso si no se puede: operación sin ejecutar o
   * ya cerrada, nivel desactivado, sin precio o del lado equivocado del mercado. Sin `stoplossinicial`
   * (reentradas de Sincronizar) se usa el `stoploss` vigente, el mismo que llevaría la orden OCO.
   */
  private validarNivel(el: any, lado: 'sl' | 'tp', ctx: SlTpContexto): number | null {
    const nombre = lado === 'sl' ? 'stop loss' : 'profit';
    const omitir = (msj: string) => { this.toastr.warning(msj, 'Orden omitida'); return null; };
    if (el.estado !== 'FILLED') return omitir('El activo ' + el.isooper + ' no está ejecutado (FILLED).');
    if (this.tieneSalida(ctx, el)) {
      this.toastr.error('El activo ' + el.isooper + ' ya tiene una salida registrada.', 'Orden omitida');
      return null;
    }
    // carterastoplossactivo sale de la subcartera de la orden: sin cartera enlazada no se sabe y no se bloquea
    if (lado === 'sl' ? (el.cartera && !el.carterastoplossactivo) : !el.carteraprofitactivo) {
      return omitir('La cartera de ' + el.isooper + ' no tiene el ' + nombre + ' activo.');
    }
    const precio = lado === 'sl'
      ? (Number(el.stoplossinicial) > 0 ? Number(el.stoplossinicial) : Number(el.stoploss))
      : (Number(el.profitinicial) > 0 ? Number(el.profitinicial) : Number(el.profit));
    if (!(precio > 0)) return omitir('El activo ' + el.isooper + ' no tiene precio de ' + nombre + ' definido.');
    const redondeado = this.redondear(precio, el, ctx);
    if (!this.ladoCorrecto(el, lado, redondeado)) {
      return omitir('El ' + nombre + ' de ' + el.isooper + ' (' + redondeado + ') no es válido con el precio actual (' + this.precioMercado(el) + ').');
    }
    return redondeado;
  }

  private precioMercado(el: any): number {
    return Number(el.precioactual) || Number(el.cotizacion) || 0;
  }

  /** El SL de un alcista y el TP de un bajista van por debajo del mercado; los otros, por encima. */
  private ladoCorrecto(el: any, lado: 'sl' | 'tp', precio: number): boolean {
    const mercado = this.precioMercado(el);
    if (!(mercado > 0)) return true;
    return (lado === 'sl') !== !!el.bajista ? precio < mercado : precio > mercado;
  }

  /** Orden STP (IBKR) o stopLoss de la posición (MetaAPI) de cada operación FILLED. */
  async establecerSL(elements: any[], ctx: SlTpContexto): Promise<void> {
    // MetaTrader con Algo Trading desactivado: aviso y no se envía nada
    if (!(await lastValueFrom(this.brokerAdapterFactory.puedeOperar(ctx.tipoBroker)))) return;
    for (let i = 0; i < elements.length; i++) {
      const compania = elements[i];
      const stopPrice = this.validarNivel(compania, 'sl', ctx);
      if (stopPrice === null) continue;

      if (ctx.tipoBroker === 'METAAPI') {
        await this.establecerNivelMetaApi(compania, 'sl', stopPrice, ctx);
        continue;
      }

      this.ibkrOrden.registrarYEnviar(this.ordenNivelIbkr(compania, 'sl', stopPrice, ctx), ctx.cartera, ctx.panelClass, 'SL_MANUAL');
      if (i < elements.length - 1) {
        await new Promise(resolve => setTimeout(resolve, 2000));
      }
    }
  }

  /** Mueve el SL colocado al stoploss dinámico actual (solo a favor de la posición). */
  /** MetaTrader con Algo Trading desactivado: aviso y no se inicia el flujo. */
  actualizarSL(elements: any[], ctx: SlTpContexto) {
    this.brokerAdapterFactory.puedeOperar(ctx.tipoBroker).subscribe(ok => { if (ok) this.actualizarSLConfirmado(elements, ctx); });
  }

  private actualizarSLConfirmado(elements: any[], ctx: SlTpContexto) {
    for (const el of elements) {
      const actual = Number(el.pricestp);
      if (!(actual > 0)) {
        this.toastr.warning('El activo ' + el.isooper + ' no tiene un stop loss colocado: use Establecer Stop Loss', 'Orden omitida');
        continue;
      }
      const newPrice = this.redondear(Number(el.stoploss) || 0, el, ctx);
      if (!(newPrice > 0) || (el.bajista ? newPrice >= actual : newPrice <= actual)) {
        this.toastr.warning('El stop loss dinámico de ' + el.isooper + ' (' + newPrice + ') no mejora el colocado (' + actual + ')', 'Orden omitida');
        continue;
      }
      if (!this.ladoCorrecto(el, 'sl', newPrice)) {
        this.toastr.warning('El stop loss de ' + el.isooper + ' (' + newPrice + ') no es válido con el precio actual (' + this.precioMercado(el) + ')', 'Orden omitida');
        continue;
      }
      if (ctx.tipoBroker === 'METAAPI') {
        const tp = Number(el.pricelmt) > 0 ? this.redondear(Number(el.pricelmt), el, ctx) : 0;
        this.modificarNivelMetaApi(this.nivelMetaApi(el, ctx), 'sl', newPrice, tp, this.wallet(ctx).accountId);
        continue;
      }
      if (!(el.orderidsalidastp && el.orderidsalidastp != '0')) {
        this.toastr.error('No se encontró la orden STP de ' + el.isooper + ' en IBKR');
        continue;
      }
      const ordennueva = {
        conid: el.conid,
        orderType: 'STP',
        price: newPrice,
        side: el.bajista ? 'BUY' : 'SELL',
        ticker: el.ticker,
        tif: 'GTC',
        quantity: el.cantidadacciones,
      };
      this.oauthService.apiPostIbrk('/iserver/account/' + ctx.cartera + '/order/' + el.orderidsalidastp, ordennueva).subscribe({
        next: (data: any) => {
          if (data.error) {
            this.toastr.error('Error al actualizar el stop loss de ' + el.isooper + ': ' + data.error);
          } else {
            this.toastr.success('Orden ' + el.ticker + ' actualizada correctamente');
            this.actualizarPrecioIbkr(el.orderidsalidastp);
          }
        }
      });
    }
  }

  /** Quita la orden STP (IBKR) o el stopLoss de la posición (MetaAPI). */
  /** MetaTrader con Algo Trading desactivado: aviso y no se inicia el flujo. */
  cancelarSL(elements: any[], ctx: SlTpContexto) {
    this.brokerAdapterFactory.puedeOperar(ctx.tipoBroker).subscribe(ok => { if (ok) this.cancelarSLConfirmado(elements, ctx); });
  }

  private cancelarSLConfirmado(elements: any[], ctx: SlTpContexto) {
    for (const el of elements) {
      if (ctx.tipoBroker === 'METAAPI') {
        // positionId en fresco: orderidsalidastp puede ser de otra posición del mismo isooper
        this.conPositionId(this.nivelMetaApi(el, ctx), (positionId) => {
          const trade: any = { actionType: 'POSITION_MODIFY', positionId, stopLoss: 0 };
          if (el.pricelmt > 0) trade.takeProfit = this.redondear(el.pricelmt, el, ctx);
          this.cancelarNivelMetaApi(el, 'STP', trade, ctx);
        });
      } else if (el.orderidsalidastp != '0') {
        this.cancelarOrdenIbkr(el.orderidsalidastp, ctx, () => this.toastr.success('Orden ' + el.ticker + ' cancelada correctamente'),
          () => this.toastr.error('Error al cancelar el stop loss de ' + el.ticker));
      } else {
        this.toastr.error('No se puede cancelar la orden ' + el.ticker + ' porque no es una orden STP');
      }
    }
  }

  /** Orden LMT (IBKR) o takeProfit de la posición (MetaAPI) de cada operación FILLED con profit activo. */
  /** MetaTrader con Algo Trading desactivado: aviso y no se inicia el flujo. */
  establecerTP(elements: any[], ctx: SlTpContexto) {
    this.brokerAdapterFactory.puedeOperar(ctx.tipoBroker).subscribe(ok => { if (ok) this.establecerTPConfirmado(elements, ctx); });
  }

  private async establecerTPConfirmado(elements: any[], ctx: SlTpContexto) {
    for (const compania of elements) {
      const profitPrice = this.validarNivel(compania, 'tp', ctx);
      if (profitPrice === null) continue;
      if (ctx.tipoBroker === 'METAAPI') {
        await this.establecerNivelMetaApi(compania, 'tp', profitPrice, ctx);
        continue;
      }
      this.ibkrOrden.registrarYEnviar(this.ordenNivelIbkr(compania, 'tp', profitPrice, ctx), ctx.cartera, ctx.panelClass, 'TP_MANUAL');
    }
  }

  /** Quita la orden LMT (IBKR) o el takeProfit de la posición (MetaAPI). Quitar siempre se permite. */
  /** MetaTrader con Algo Trading desactivado: aviso y no se inicia el flujo. */
  cancelarTP(elements: any[], ctx: SlTpContexto) {
    this.brokerAdapterFactory.puedeOperar(ctx.tipoBroker).subscribe(ok => { if (ok) this.cancelarTPConfirmado(elements, ctx); });
  }

  private cancelarTPConfirmado(elements: any[], ctx: SlTpContexto) {
    for (const el of elements) {
      if (ctx.tipoBroker === 'METAAPI') {
        this.conPositionId(this.nivelMetaApi(el, ctx), (positionId) => {
          const trade: any = { actionType: 'POSITION_MODIFY', positionId, takeProfit: 0 };
          if (el.pricestp > 0) trade.stopLoss = this.redondear(el.pricestp, el, ctx);
          this.cancelarNivelMetaApi(el, 'LMT', trade, ctx);
        });
      } else if (el.orderidsalidalmt != '0') {
        this.cancelarOrdenIbkr(el.orderidsalidalmt, ctx, () => this.toastr.success('Take Profit de ' + el.ticker + ' cancelado'),
          () => this.toastr.error('Error al cancelar TP de ' + el.ticker));
      } else {
        this.toastr.error('No se puede cancelar: la orden no es LMT (TP).');
      }
    }
  }

  /**
   * MetaAPI: los decimales del símbolo (spec.digits) si su especificación ya está en caché.
   * Si no, o en IBKR: 5 para CFD y 2 para el resto.
   */
  private redondear(valor: number, el: any, ctx?: SlTpContexto): number {
    if (ctx?.tipoBroker === 'METAAPI') {
      const simbolo = el.nombrecompania?.split(' CFD')[0] || el.ticker;
      const digits = this.metaApi.digitsEnCache(this.wallet(ctx).accountId, simbolo);
      if (digits !== null) return Number(valor.toFixed(digits));
    }
    return el.secType === 'CFD' ? Number(valor.toFixed(5)) : Number(valor.toFixed(2));
  }

  private cOID(el: any): string {
    return 'nctminvst' + Date.now() + el.idNectum + '-' + el.isooper + ':' + (el.bajista ? 'B' : 'A');
  }

  /** IBKR: STP (SL) o LMT (TP) GTC suelta, del lado que cierra la posición. */
  private ordenNivelIbkr(el: any, lado: 'sl' | 'tp', precio: number, ctx: SlTpContexto): { orders: any[] } {
    return {
      orders: [{
        orderType: lado === 'sl' ? 'STP' : 'LMT',
        ticker: el.nombrecompania?.split(' CFD')[0] || el.ticker,
        side: el.bajista ? 'BUY' : 'SELL',
        quantity: el.cantidadacciones,
        remainingquantity: 0,
        acctId: ctx.cartera,
        cOID: this.cOID(el),
        isSingleGroup: true,
        secType: el.conid + ':' + el.secType,
        filledquantity: 0,
        parentId: '',
        tif: 'GTC',
        outsideRTH: false,
        referrer: 'modelosdeinversion',
        useAdaptive: false,
        conid: el.conid,
        price: precio
      }]
    };
  }

  /** Datos de la posición para localizarla en MetaAPI: la cuenta es el login MT5 (wallet.nombre). */
  private nivelMetaApi(el: any, ctx: SlTpContexto): any {
    return { ...el, ticker: el.ticker || el.nombrecompania?.split(' CFD')[0], cuenta: ctx.cartera };
  }

  /**
   * MetaAPI: pone el SL o el TP en la posición (POSITION_MODIFY conservando el otro nivel vivo) y lo
   * registra como orden STP/LMT con orderId = positionId. Si ya tiene ese nivel, lo mueve con
   * `modificarNivelMetaApi`: registrar otro lo rechaza la BD (ya hay una STP/LMT pendiente).
   */
  private establecerNivelMetaApi(el: any, lado: 'sl' | 'tp', precio: number, ctx: SlTpContexto): Promise<void> {
    const { accountId, wallet } = this.wallet(ctx);
    const nivel = this.nivelMetaApi(el, ctx);
    const otro = Number(lado === 'sl' ? el.pricelmt : el.pricestp);
    const otroPrecio = otro > 0 ? this.redondear(otro, el, ctx) : 0;
    if (Number(lado === 'sl' ? el.pricestp : el.pricelmt) > 0) {
      this.modificarNivelMetaApi(nivel, lado, precio, otroPrecio, accountId);
      return Promise.resolve();
    }
    const etiqueta = lado === 'sl' ? 'STP' : 'TP';
    return new Promise<void>(resolve => {
      this.conPositionId(nivel, (positionId) => {
        const trade: any = { actionType: 'POSITION_MODIFY', positionId };
        trade[lado === 'sl' ? 'stopLoss' : 'takeProfit'] = precio;
        // Sin el otro nivel, POSITION_MODIFY lo quitaría de la posición
        if (otroPrecio > 0) trade[lado === 'sl' ? 'takeProfit' : 'stopLoss'] = otroPrecio;
        this.brokerAdapterFactory.getAdapter(wallet).placeOrder(accountId, trade).subscribe({
          next: (res: any) => {
            // NO_CHANGES: la posición ya tiene ese nivel (p. ej. un intento anterior que no se registró):
            // se registra igual para que la BD quede como el broker
            const rechazo = res?.stringCode === 'TRADE_RETCODE_NO_CHANGES' ? null : motivoRechazoMetaApi(res);
            if (rechazo) {
              this.toastr.error('Error MetaAPI al enviar ' + etiqueta + ' de ' + el.isooper + ': ' + rechazo);
              resolve();
              return;
            }
            const cOID = this.cOID(el);
            const orderRecord = [{
              ticker: nivel.ticker,
              side: el.bajista ? 'BUY' : 'SELL',
              quantity: el.cantidadacciones,
              acctId: ctx.cartera,
              orderType: lado === 'sl' ? 'STP' : 'LMT',
              price: precio,
              orderId: positionId,
              status: 'Submitted',
              secType: el.conid + ':' + el.secType,
              cOID,
              // Hija de la entrada, como el SL/TP de una OCO. Sin parentId el backend la trata como
              // orden suelta y la descarta si hay otra pendiente del mismo lado: en bajista el SL y el
              // TP son ambos BUY, así que el TP vivo bloqueaba registrar el SL (y al revés).
              parentId: el.order_ref || '',
              origen: (lado === 'sl' ? 'SL_MANUAL' : 'TP_MANUAL') as OrigenOrden,
            }];
            const noRegistrado = (msg?: string) => this.toastr.warning(etiqueta + ' de ' + el.isooper
              + ' colocado en el broker, pero no se pudo registrar en el sistema' + (msg ? ': ' + msg : ''));
            this.sse.apiPostNew('broker/orderNuevo', orderRecord, this.licencia).subscribe({
              next: (r: any) => {
                if (r?.status) this.toastr.success(etiqueta + ' enviado: ' + el.isooper);
                else noRegistrado(r?.msg);
                this.brokerService.updatecargarListas(true);
                resolve();
              },
              error: () => { noRegistrado(); resolve(); }
            });
          },
          error: (err: any) => { this.toastr.error('Error MetaAPI al enviar ' + etiqueta + ': ' + (err?.message ?? err)); resolve(); }
        });
      }, () => resolve());
    });
  }

  private cancelarOrdenIbkr(orderId: any, ctx: SlTpContexto, onOk: () => void, onError?: () => void): void {
    this.oauthService.apiDeleteIbrk('/iserver/account/' + ctx.cartera + '/order/' + orderId).subscribe({
      next: (data: any) => {
        if (data?.error) {
          console.log('error al cancelar orden', data.error);
          onError?.();
        } else {
          onOk();
          this.actualizarPrecioIbkr(orderId);
        }
      }
    });
  }

  private actualizarPrecioIbkr(orderId: any): void {
    this.oauthService.apiGetIbrk('/iserver/account/order/status/' + orderId).subscribe({
      next: (st: any) => {
        this.sse.apiPostNew('broker/actualizarOrdenPrecio', st, this.licencia).subscribe({
          next: (r: any) => { if (r.status) this.brokerService.updatecargarListas(true); }
        });
      }
    });
  }

  /**
   * MetaAPI: mueve el SL o el TP de una posición a `precio` con POSITION_MODIFY (el otro nivel se conserva
   * si es > 0) y guarda el precio en BD. `el` = { isooper, ticker, positionId?, bajista?, cuenta? }.
   */
  modificarNivelMetaApi(el: any, lado: 'sl' | 'tp', precio: number, otroPrecio: number, accountId: string): void {
    const etiqueta = lado === 'sl' ? 'Stop Loss' : 'Take Profit';
    const enviar = (positionId: string) => {
      const trade: any = { actionType: 'POSITION_MODIFY', positionId };
      trade[lado === 'sl' ? 'stopLoss' : 'takeProfit'] = precio;
      if (otroPrecio > 0) trade[lado === 'sl' ? 'takeProfit' : 'stopLoss'] = otroPrecio;
      const wallet: any = { tipoBroker: 'METAAPI', metaApiAccountId: accountId, nombre: accountId };
      this.brokerAdapterFactory.getAdapter(wallet).placeOrder(accountId, trade).subscribe({
        next: (res: any) => {
          const rechazo = motivoRechazoMetaApi(res);
          if (rechazo) {
            this.toastr.error('Error al modificar ' + etiqueta + ' MetaAPI: ' + rechazo);
            return;
          }
          const tipo = lado === 'sl' ? 'STP' : 'LMT';
          const st = { order_id: Number(positionId), order_type: tipo, stop_price: String(precio), limit_price: String(precio) };
          this.sse.apiPostNew('metaapi/modificarNivel', st, this.licencia).subscribe({
            next: (r: any) => {
              if (r?.status === false) this.toastr.warning(etiqueta + ' modificado en el broker, pero no se pudo actualizar en el sistema');
              else this.toastr.success(etiqueta + ' de ' + el.ticker + ' modificado');
              this.brokerService.updatecargarListas(true);
            },
            error: () => {
              this.toastr.warning(etiqueta + ' modificado en el broker, pero no se pudo actualizar en el sistema');
              this.brokerService.updatecargarListas(true);
            }
          });
        },
        error: (err: any) => this.toastr.error('Error al modificar ' + etiqueta + ' MetaAPI: ' + (err?.message ?? err))
      });
    };
    if (el.positionId && String(el.positionId) !== '0') enviar(String(el.positionId));
    else this.conPositionId(el, enviar);
  }

  /**
   * positionId de la entrada de la operación. `cuenta` = login MT5 (tbl_orden.acctid); sin ella, el de la
   * sesión. Con `bajista` se filtra por sentido: el isooper puede repetirse entre alcista y bajista.
   */
  private conPositionId(el: any, fn: (positionId: string) => void, onFallo?: () => void): void {
    const body: any = { accountId: el.cuenta || localStorage.getItem('meta_api_account_login'), isooper: el.isooper };
    if (el.bajista !== undefined && el.bajista !== null) body.bajista = !!el.bajista;
    this.sse.apiPostNew('metaapi/obtenerpositionid', body, this.licencia).subscribe({
      next: (result: any) => {
        if (!result?.positionId) {
          this.toastr.error('No se encontró posición para ' + (el.ticker || el.isooper));
          onFallo?.();
          return;
        }
        fn(String(result.positionId));
      },
      error: (err: any) => {
        this.toastr.error('Error obteniendo positionId: ' + (err?.message ?? err));
        onFallo?.();
      }
    });
  }

  /**
   * Quita el SL ('STP') o el TP ('LMT') de la posición MetaAPI (POSITION_MODIFY ya armado en `trade`)
   * y marca en BD la orden sintética como cancelada. La BD guarda el STP/LMT con order_id = positionId.
   */
  private cancelarNivelMetaApi(el: any, nivel: 'STP' | 'LMT', trade: any, ctx: SlTpContexto): void {
    const { accountId, wallet } = this.wallet(ctx);
    const etiqueta = nivel === 'STP' ? 'Stop Loss' : 'Take Profit';
    this.brokerAdapterFactory.getAdapter(wallet).placeOrder(accountId, trade).subscribe({
      next: (res: any) => {
        if (res?.error || (res?.stringCode && res.stringCode !== 'TRADE_RETCODE_DONE')) {
          this.toastr.error('Error al cancelar ' + etiqueta + ' MetaAPI: ' + (res.message ?? res.stringCode ?? res.error));
          return;
        }
        const st = { order_id: Number(trade.positionId), price: 0, pricelmt: 0, order_type: nivel };
        this.sse.apiPostNew('metaapi/cancelarOrden', st, this.licencia).subscribe({
          next: (r: any) => {
            if (r?.status === false) {
              console.error('metaapi/cancelarOrden', st, r);
              this.toastr.error(etiqueta + ' cancelado en el broker, pero no se pudo actualizar en el sistema: ' + (r.message ?? 'sin detalle'));
            } else this.toastr.success(etiqueta + ' de ' + el.ticker + ' cancelado correctamente');
            this.brokerService.updatecargarListas(true);
          },
          error: (err: any) => {
            console.error('metaapi/cancelarOrden', st, err);
            this.toastr.error(etiqueta + ' cancelado en el broker, pero no se pudo actualizar en el sistema: ' + (err?.error?.message ?? err?.message ?? err));
            this.brokerService.updatecargarListas(true);
          }
        });
      },
      error: (err: any) => this.toastr.error('Error al cancelar ' + etiqueta + ' MetaAPI: ' + (err?.message ?? err))
    });
  }
}
